import { useAppSelector } from "@/hooks/useAppSelector";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ComposedChart, Line } from "recharts";

export const YearlyPriceIncreasesGraph = () => {
  const { events } = useAppSelector(state => state.events);

  // Process events data to count unique price increases and total unique companies by year
  const yearStats = events
    .filter(event => !event.error) // Filter out events with errors
    .reduce((acc: { 
      [key: string]: { 
        increases: Set<string>; // Set of slugs with price increases
        total: Set<string>;    // Set of all slugs
      } 
    }, event) => {
      const year = event.v1.substring(0, 4);
      
      // Only include years 2023 and 2024
      if (year !== '2023' && year !== '2024') return acc;

      // Initialize year stats if not exists
      if (!acc[year]) {
        acc[year] = { 
          increases: new Set<string>(),
          total: new Set<string>()
        };
      }

      // Add slug to total companies for the year
      acc[year].total.add(event.slug);

      // If price increased, add slug to increases set
      if (event.priceIncreased) {
        acc[year].increases.add(event.slug);
      }

      return acc;
    }, {});

  // Convert to array format for Recharts
  const chartData = ['2023', '2024'].map(year => {
    const stats = yearStats[year] || { increases: new Set(), total: new Set() };
    const percentage = stats.total.size > 0 
      ? (stats.increases.size / stats.total.size) * 100 
      : 0;
    
    return {
      year,
      increases: stats.increases.size,
      percentage: Number(percentage.toFixed(1))
    };
  });

  return (
    <div className="grid grid-cols-2 gap-8">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-black">Unique Price Increases by Year</h3>
        <div className="flex flex-col items-start w-full">
          <div className="w-full h-[300px]">
            <ChartContainer
              config={{
                bar: {
                  theme: {
                    light: "#ef4444",
                    dark: "#ef4444",
                  },
                },
              }}
            >
              <BarChart 
                data={chartData} 
                margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
              >
                <XAxis
                  dataKey="year"
                  tick={{ fill: '#000000', fontSize: 12 }}
                  stroke="#000000"
                />
                <YAxis
                  tick={{ fill: '#000000', fontSize: 12 }}
                  stroke="#000000"
                  label={{ 
                    value: 'Number of Companies', 
                    angle: -90, 
                    position: 'insideLeft', 
                    fill: '#000000'
                  }}
                />
                <Bar 
                  dataKey="increases" 
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]} 
                />
                <ChartTooltip />
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-black">Percentage of Companies with Price Increases by Year</h3>
        <div className="flex flex-col items-start w-full">
          <div className="w-full h-[300px]">
            <ChartContainer
              config={{
                line: {
                  theme: {
                    light: "#2563eb",
                    dark: "#2563eb",
                  },
                },
              }}
            >
              <ComposedChart 
                data={chartData} 
                margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
              >
                <XAxis
                  dataKey="year"
                  tick={{ fill: '#000000', fontSize: 12 }}
                  stroke="#000000"
                />
                <YAxis
                  tick={{ fill: '#000000', fontSize: 12 }}
                  stroke="#000000"
                  label={{ 
                    value: 'Percentage (%)', 
                    angle: -90, 
                    position: 'insideLeft', 
                    fill: '#000000'
                  }}
                />
                <Line 
                  type="monotone"
                  dataKey="percentage"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ fill: '#2563eb' }}
                />
                <ChartTooltip />
              </ComposedChart>
            </ChartContainer>
          </div>
        </div>
      </div>
    </div>
  );
};